import { useTranslation } from 'react-i18next';
import { LogOut, Moon, Sun } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { useTheme } from '@/lib/theme';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { LanguageSwitcher } from './LanguageSwitcher';

function initials(label: string): string {
  const parts = label.trim().split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

export function TopBar(): JSX.Element {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const label = user?.displayName ?? user?.email ?? '—';
  const apiHost = import.meta.env.VITE_API_BASE_URL?.replace(/^https?:\/\//, '') ?? t('topbar.noApi');

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border/70 bg-background/80 px-4 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60">
      <WorkspaceSwitcher />
      <div className="flex-1" />
      <span className="hidden items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground shadow-xs sm:inline-flex">
        <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
        <span className="font-mono text-[11px]">{apiHost}</span>
      </span>
      <LanguageSwitcher />
      <Button
        variant="ghost"
        size="icon"
        onClick={toggle}
        aria-label={theme === 'dark' ? t('topbar.themeLight') : t('topbar.themeDark')}
      >
        {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>
      <div className="mx-1 hidden h-6 w-px bg-border sm:block" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2 pl-1.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-primary to-violet-500 text-[11px] font-semibold text-primary-foreground">
              {initials(label)}
            </span>
            <span className="hidden max-w-[12rem] truncate text-sm md:inline">{label}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="flex flex-col">
              <span>{user?.email ?? '—'}</span>
              {user?.displayName ? (
                <span className="text-xs font-normal text-muted-foreground">
                  {user.displayName}
                </span>
              ) : null}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={logout} className="cursor-pointer">
            <LogOut className="mr-2 h-4 w-4" />
            <span>{t('topbar.logout')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
